import CampaignsIcon from "@/assets/colorable/campaigns.svg?react";
import ChairIcon from "@/assets/colorable/chair.svg?react";
import CycleIcon from "@/assets/colorable/cycle.svg?react";
import EmailPlusIcon from "@/assets/colorable/email_plus.svg?react";
import InfiniteIcon from "@/assets/colorable/infinite.svg?react";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import BuyoutIcon from "@/assets/colorable/source_content.svg?react";
import usePackageLimits from "@/hooks/usePackageLimits";
import {useAgencySeats, useCampaignsLeft, useIsSubbrand} from "@/hooks/useUserHooks";
import {INFINITY_THRESHOLD} from "@/store/constants";
import {isMoreBuyoutsModalOpenAtom, isRequestMoreResourcesModalOpenAtom} from "@/store/jotaiAtoms";
import type {TFloatingBuyouts} from "@/store/types";
import dayjs from "dayjs";
import {useSetAtom} from "jotai";
import {FC, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Tooltip} from "react-tooltip";

const PlanDetails: FC<{
  type: TFloatingBuyouts;
}> = ({type}) => {
  const {t} = useTranslation();
  const {isOwnProfile, remainingBuyouts, packageIncludedBuyouts, validUntil} = usePackageLimits();
  const campaignsLeft = useCampaignsLeft();
  const seats = useAgencySeats();
  const isSubbrand = useIsSubbrand();
  const isRequest = useMemo(
    () =>
      isSubbrand && ((type === "buyouts" && remainingBuyouts === 0) || (type === "campaigns" && campaignsLeft === 0)),
    [isSubbrand, type, remainingBuyouts, campaignsLeft],
  );
  // TODO: add casl
  const setMoreBuyoutsModalOpen = useSetAtom(isMoreBuyoutsModalOpenAtom);
  const openMoreModal = useSetAtom(isRequestMoreResourcesModalOpenAtom);

  return (
    <div className="border-neutral-white-dark flex h-fit w-fit origin-bottom-left flex-col gap-3 rounded-md border bg-white py-3 shadow-xl drop-shadow-lg transition-all duration-500 ease-in-out">
      <div className="flex items-center justify-between px-3">
        {type === "buyouts" && (
          <div className="flex items-center gap-2 py-1" data-tooltip-id="remaining-buyouts" data-tip>
            <Tooltip id="remaining-buyouts" place="top" variant="dark" delayShow={500}>
              {t("contentBuyoutsRemaining")}
            </Tooltip>
            <BuyoutIcon className="text-neutral-black size-3.5" />
            {remainingBuyouts > INFINITY_THRESHOLD ? (
              <InfiniteIcon className="text-neutral-black h-4 w-5" />
            ) : (
              <span className="text-neutral-black font-semibold">{remainingBuyouts}</span>
            )}
          </div>
        )}
        {type === "seats" && (
          <div className="flex items-center gap-2 py-1" data-tooltip-id="remaining-seats" data-tip>
            <Tooltip id="remaining-seats" place="top" variant="dark" delayShow={500}>
              {t("contentBuyoutsRemaining")}
            </Tooltip>
            <ChairIcon className="text-neutral-black size-3.5" />
            {!!seats && seats > INFINITY_THRESHOLD ? (
              <InfiniteIcon className="text-neutral-black h-4 w-5" />
            ) : (
              <span className="text-neutral-black font-semibold">{seats}</span>
            )}
          </div>
        )}
        {type === "campaigns" && (
          <div className="flex items-center gap-2 py-1" data-tooltip-id="remaining-seats" data-tip>
            <Tooltip id="remaining-seats" place="top" variant="dark" delayShow={500}>
              {t("contentBuyoutsRemaining")}
            </Tooltip>
            <CampaignsIcon className="text-neutral-black size-3.5" />
            {!!campaignsLeft && campaignsLeft! > INFINITY_THRESHOLD ? (
              <InfiniteIcon className="text-neutral-black h-4 w-5" />
            ) : (
              <span className="text-neutral-black font-semibold">{campaignsLeft}</span>
            )}
          </div>
        )}
        {isOwnProfile && (
          <button
            className="group hover:bg-neutral-white-dark rounded-md p-1.5 transition-all duration-150"
            onClick={() => {
              if (isRequest) {
                return openMoreModal({
                  show: true,
                  type,
                });
              }

              if (type === "buyouts") {
                setMoreBuyoutsModalOpen({show: true, type: "EXTRA_BUYOUTS"});
              } else if (type === "seats") {
                setMoreBuyoutsModalOpen({show: true, type: "EXTRA_SEATS"});
              } else {
                setMoreBuyoutsModalOpen({
                  show: true,
                  type: "EXTRA_CAMPAIGNS",
                });
              }
            }}>
            {isRequest && <EmailPlusIcon className="text-primary-dark-solid size-4 transition-all duration-150" />}
            {!isSubbrand && (
              <PlusIcon className="text-neutral-black group-hover:text-primary-dark-solid size-4 transition-all duration-150" />
            )}
          </button>
        )}
      </div>
      <div className="bg-neutral-white-dark h-[1px] w-full" />
      <div data-tooltip-id="reset-buyouts" className="flex items-center justify-start gap-2 px-3">
        <Tooltip id="reset-buyouts" place="right" variant="light" delayShow={500}>
          {t("buyoutsWillBeResetted", {buyoutCount: packageIncludedBuyouts})}
        </Tooltip>
        <CycleIcon className="text-neutral-grey-dark h-2.5 w-2.5" />
        <span className="text-neutral-grey-dark text-[10px]">{dayjs(validUntil).format("DD. MMMM YYYY.")}</span>
      </div>
    </div>
  );
};

export default PlanDetails;
